import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import { ResourceRequestAccessLevel } from '~/types';
import { USER_DAL } from '~/modules/common/enums';
import { OVERVIEW_UNITS_ENUM } from '~/modules/resource-management/common/enums';
import {
  convertResourceAllocationSeriesDataToFTEUnits,
  convertResourceAllocationSeriesDataToPercentageUnits
} from '~/modules/resource-management/ResourcingOverview/components/ResourceAllocationChart/hooks/fte-conversion';
import { PERIOD_RESOLUTION_ENUM } from '~/modules/resourcing/common/enums';

export const actionItemsAllocationDataQuery = gql`
  query ActionItems_ResourceAllocationSeriesData(
    $dateRange: DateRangeInput!
    $periodResolution: PeriodResolutionOption!
    $filter: ResourceAllocationSeriesFilter
    $requestAccessLevel: ResourceRequestAccessLevel
    $resourceAllocationDataAccessLevelUris: [String!]
  ) {
    resourceAllocationSeriesData(
      dateRange: $dateRange
      periodResolution: $periodResolution
      filter: $filter
      requestAccessLevel: $requestAccessLevel
      resourceAllocationDataAccessLevelUris: $resourceAllocationDataAccessLevelUris
    ) {
      totalAvailable
      overAllocated
      requested
      toBeHired
      proposed
      totalSchedule
    }
  }
`;

const convertDataByDisplayUnit = ({ data, displayUnit, dateRange }) => {
  if (!data) return null;
  const dataWithDateRange = data.map(item => ({
    ...item,
    dateRange
  }));

  switch (displayUnit) {
    case OVERVIEW_UNITS_ENUM.FTE:
      return convertResourceAllocationSeriesDataToFTEUnits(dataWithDateRange);
    case OVERVIEW_UNITS_ENUM.PERCENTAGE:
      return convertResourceAllocationSeriesDataToPercentageUnits({
        resourceAllocationSeriesData: dataWithDateRange,
        includeToBeHiredTotalHours: true
      });
    default:
      return data;
  }
};

export const useGetActionItemsData = ({
  dateRange,
  periodScale,
  displayUnit,
  filter
}) => {
  const { data, loading } = useQuery(actionItemsAllocationDataQuery, {
    variables: {
      dateRange: {
        startDate: dateRange.startDate.toISODate(),
        endDate: dateRange.endDate.toISODate()
      },
      periodResolution: PERIOD_RESOLUTION_ENUM[periodScale],
      filter,
      requestAccessLevel: ResourceRequestAccessLevel.ResourceManager,
      resourceAllocationDataAccessLevelUris: [
        USER_DAL.RESOURCE_MANAGER,
        USER_DAL.RESOURCE_POOL_MANAGER
      ]
    },
    fetchPolicy: 'cache-and-network'
  });

  const convertedActionItemData = convertDataByDisplayUnit({
    data: data?.resourceAllocationSeriesData,
    displayUnit,
    dateRange
  })?.[0];

  return {
    loading,
    actionItemValues: {
      bench: convertedActionItemData?.totalAvailable,
      overAllocated: convertedActionItemData?.overAllocated,
      toBeHired: convertedActionItemData?.toBeHired,
      pendingRequests: convertedActionItemData?.requested,
      proposedResources: convertedActionItemData?.proposed
    }
  };
};

export default useGetActionItemsData;
